import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  BodyWrapper,
  ActionObject,
  Header,
  Loader,
  MainWrapper,
} from '@components';
import { useDatamodel } from '@hooks';
import {
  authorsQuery,
  blogCategoriesQuery,
  businessQuery,
  postDeleteMutation,
  postMutation,
  postQuery,
  siteQuery,
} from '@queries';
import { ChevronRightIcon } from '@svgs';

import PostForm from '../../Site/components/PostForm/PostForm.jsx';
import { TemplateBreadcrumbs } from '../components/TemplateBreadcrumbs/TemplateBreadcrumbs.jsx';
import { TemplateSidebar } from '../components/TemplateSidebar/TemplateSidebar.jsx';

export const TemplatePost = () => {
  const { t } = useTranslation();
  const { businessID, postID, siteID } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: businessData, isPending: businessIsPending } = useQuery(
    businessQuery(businessID),
  );

  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(siteID),
  );

  const {
    data: postData,
    isPending: postIsPending,
    isError,
  } = useQuery(postQuery(postID));

  const { data: authorsData, isPending: authorsIsPending } = useQuery(
    authorsQuery({
      limit: 100,
      order_by: 'name',
      site_id: siteID,
    }),
  );

  const { data: blogCategoriesData, isPending: blogCategoriesIsPending } =
    useQuery(
      blogCategoriesQuery({
        limit: 100,
        site_id: siteID,
      }),
    );

  const {
    datamodelData,
    isPending: isCustomBlogsPending,
    relatedDatamodels,
  } = useDatamodel('blog_custom_fields');

  const mutation = useMutation(
    postMutation(postID, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['posts', 'list'] });
        queryClient.invalidateQueries({
          queryKey: ['posts', 'detail', postID],
        });
      },
    }),
  );

  const deleteMutation = useMutation(
    postDeleteMutation(postID, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['posts', 'list'] });
        navigate(`/dashboard/projects/${businessID}/templates/${siteID}/blogs`);
      },
    }),
  );

  const modifyData = (data) => {
    return {
      ...data,
      authors: data.authors.map((author) => author.id),
      categories: data.categories
        .filter((c) => !c.is_post_type)
        .map((c) => c.id),
      post_type_category: data.categories.find((c) => c.is_post_type)?.id,
    };
  };

  const blogsPath = `/dashboard/projects/${businessID}/templates/${siteID}/blogs`;

  const isPending =
    businessIsPending ||
    siteIsPending ||
    postIsPending ||
    authorsIsPending ||
    blogCategoriesIsPending ||
    isCustomBlogsPending;

  return (
    <>
      <Header />
      <BodyWrapper>
        <TemplateSidebar
          businessID={businessID}
          businessData={businessData}
          siteID={siteID}
          siteData={siteData}
        />
        <MainWrapper>
          <TemplateBreadcrumbs businessData={businessData} siteData={siteData}>
            <li className="sb-flex">
              <Link to={blogsPath}>{t('Blog posts')}</Link>
              <ChevronRightIcon />
            </li>
            <li className="sb-flex">
              {postIsPending && <span>...</span>}
              {postData && <span>{postData.title}</span>}
            </li>
          </TemplateBreadcrumbs>
          <div className="sb-manage-content">
            {isPending && (
              <>
                <h1>...</h1>
                <Loader className="sb-loader sb-loader-large" />
              </>
            )}
            {siteData &&
              postData &&
              authorsData &&
              blogCategoriesData &&
              !isPending &&
              !isError && (
                <>
                  <PostForm
                    postData={modifyData(postData)}
                    authorsData={authorsData}
                    categoriesData={blogCategoriesData}
                    mutation={mutation}
                    disabled={mutation.isPending || deleteMutation.isPending}
                    siteID={siteID}
                    siteData={siteData}
                    datamodelData={datamodelData}
                    relatedDatamodels={relatedDatamodels}
                  />
                  <div className="sb-message-action-section">
                    <ActionObject
                      onAction={() => deleteMutation.mutate()}
                      loading={deleteMutation.isPending}
                      actionName="Delete"
                      actionVerb="Deleting"
                      label="Delete post"
                      note="This post will be permanently deleted"
                    />
                  </div>
                </>
              )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
